import { useQuery, gql } from '@apollo/client';
import { Container, Header, Label, Loader, Message, Segment, Table } from 'semantic-ui-react';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { parseApiDate } from '../../../utils/date';

/**
 * What happened to the posts you wrote.
 *
 * **The gap this closes:** `postsForReview` and `scheduledPosts` are both gated on the
 * REVIEW_POSTS capability, so until now a submitter could see nothing at all — not "pending",
 * not "approved, going out Tuesday", not "rejected, and here is why". They submitted into a
 * void and had to ask somebody.
 *
 * That was survivable while only reviewers and leaders composed. It stopped being survivable
 * with the field-contributor role, whose entire output goes through review by design.
 *
 * Open to everyone and gated on nothing: the query returns only the caller's own posts, so
 * there is no wider set to protect. It spans every organisation they belong to, for the same
 * reason `postingTargets` does — what you wrote is a property of your memberships, not of a
 * current organisation.
 */
const MY_POSTS = gql`
  query MyPosts {
    myPosts {
      id
      title
      description
      type
      organisationName
      streamName
      publishAt
      createdAt
      status
      rejectionReason
      reviewerName
    }
  }
`;

type AuthoredPost = {
    id: number;
    title: string | null;
    description: string | null;
    type: string | null;
    organisationName: string | null;
    streamName: string | null;
    publishAt: string | null;
    createdAt: string | null;
    status: string;
    rejectionReason: string | null;
    reviewerName: string | null;
};

const STATUS: Record<string, { colour: any; label: string }> = {
    PENDING: { colour: 'yellow', label: 'Waiting for review' },
    SCHEDULED: { colour: 'blue', label: 'Scheduled' },
    PUBLISHED: { colour: 'green', label: 'Published' },
    REJECTED: { colour: 'red', label: 'Rejected' },
};

const when = (post: AuthoredPost) => {
    const date = parseApiDate(post.publishAt) ?? parseApiDate(post.createdAt);
    if (date == null) return '—';

    return date.toLocaleString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

const MyPosts = () => {
    const { data, loading, error } = useQuery(MY_POSTS, { fetchPolicy: 'cache-and-network' });
    const posts: AuthoredPost[] = data?.myPosts ?? [];

    return (
        <ResponsiveContainer>
            <Container style={{ padding: '2em 0' }}>
                <Header as="h1">My posts</Header>
                <p style={{ color: 'rgba(0,0,0,.6)' }}>
                    Everything you have written, newest first, across every organisation you
                    belong to.
                </p>

                {loading && posts.length === 0 && <Loader active inline="centered" />}
                {error && <Message error content="Could not load your posts." />}

                {!loading && posts.length === 0 && (
                    <Segment placeholder textAlign="center">
                        <Header as="h3">Nothing yet</Header>
                        <p>Posts you write will appear here with their status.</p>
                    </Segment>
                )}

                {posts.length > 0 && (
                    <Table celled stackable>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Post</Table.HeaderCell>
                                <Table.HeaderCell>Where</Table.HeaderCell>
                                <Table.HeaderCell>When</Table.HeaderCell>
                                <Table.HeaderCell>Status</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {posts.map((post) => {
                                const status = STATUS[post.status]
                                    ?? { colour: 'grey', label: post.status };

                                return (
                                    <Table.Row key={post.id} negative={post.status === 'REJECTED'}>
                                        <Table.Cell>
                                            <strong>{post.title || 'Untitled'}</strong>
                                            <div style={{ color: 'rgba(0,0,0,.6)', fontSize: '0.9em' }}>
                                                {post.description}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            {post.organisationName}
                                            {post.streamName && (
                                                <div style={{ color: 'rgba(0,0,0,.6)', fontSize: '0.9em' }}>
                                                    {post.streamName}
                                                </div>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell>{when(post)}</Table.Cell>
                                        <Table.Cell>
                                            <Label color={status.colour}>{status.label}</Label>

                                            {/* The whole point of the screen: a rejection that
                                                says why, rather than a post that vanished. */}
                                            {post.status === 'REJECTED' && (
                                                <div style={{ marginTop: '0.6em', fontSize: '0.9em' }}>
                                                    {post.rejectionReason
                                                        ? <em>“{post.rejectionReason}”</em>
                                                        : <em>No reason was given.</em>}
                                                    {post.reviewerName && (
                                                        <div style={{ color: 'rgba(0,0,0,.6)' }}>
                                                            — {post.reviewerName}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {post.status === 'PENDING' && post.reviewerName && (
                                                <div style={{ marginTop: '0.6em', fontSize: '0.9em', color: 'rgba(0,0,0,.6)' }}>
                                                    With {post.reviewerName}
                                                </div>
                                            )}
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                )}
            </Container>
        </ResponsiveContainer>
    );
};

export { MyPosts };
